import { CarAxleType, CarRecordListForBooking, TireSeason } from '@navio/nsdk';
import { getTireDimensionError, getTireDimensionPartOutsideTypicalRange } from 'noddi-util';

type CarWithTireDimensions = Pick<CarRecordListForBooking, 'tireDimensions'>;

export type TireAxle = typeof CarAxleType.FRONT | typeof CarAxleType.REAR;

/**
 * The size the customer gave for one car and season: one its model comes with (`choice` is its index), or typed in
 * (`'TYPED'`), with `front` the size of both axles unless staggered.
 */
export type TireSizeInput = { choice: number | 'TYPED' | null; front: string; rear: string; isStaggered: boolean };

/** Typed sizes a lead saves on the car's wheel set, for the axles it has none for. */
export type TireSizesToSend = { frontTireDimension?: string; rearTireDimension?: string };

export const EMPTY_TIRE_SIZE_INPUT: TireSizeInput = { choice: null, front: '', rear: '', isStaggered: false };

/** Picking a size the car's model comes with fills both axles from it. */
export const getModelSizeInput = (index: number, { front, rear }: { front: string; rear: string }): TireSizeInput => ({
  choice: index,
  front,
  rear,
  isStaggered: front !== rear
});

// Both axles, whichever tires are picked: new tires for one axle must suit the other's.
const AXLES: TireAxle[] = [CarAxleType.FRONT, CarAxleType.REAR];

/** The size registered on the car's wheel set for the season, per axle. */
const getRegisteredSize = (car: CarWithTireDimensions, season: TireSeason, axle: TireAxle) => {
  const registered = car.tireDimensions.find((dimensions) => dimensions.season === season);
  return (axle === CarAxleType.FRONT ? registered?.front : registered?.rear) ?? null;
};

/**
 * Axles without a size registered on the car's wheel set for the season. The customer gives these; an offer cannot
 * be made without them, and the sizes the car's model comes with are only choices.
 */
export const getMissingAxles = (car: CarWithTireDimensions, season: TireSeason): TireAxle[] =>
  AXLES.filter((axle) => getRegisteredSize(car, season, axle) === null);

/**
 * The given sizes for the missing axles, saved on the car's wheel set with the request. Null until every missing
 * axle has a size the backend accepts; empty when nothing is missing.
 */
export const getSizesToSend = (missingAxles: TireAxle[], input: TireSizeInput): TireSizesToSend | null => {
  const needsFront = missingAxles.includes(CarAxleType.FRONT);
  const needsRear = missingAxles.includes(CarAxleType.REAR);
  // Both missing: one size for the whole car unless the customer says front and rear differ.
  const rear = needsFront && !input.isStaggered ? input.front : input.rear;
  const sizes = {
    ...(needsFront && { frontTireDimension: input.front }),
    ...(needsRear && { rearTireDimension: rear })
  };
  const isAccepted = (size: string) =>
    // Outside what a passenger car has is a typo far more often than a real size; the field says which part.
    getTireDimensionError(size) === null && getTireDimensionPartOutsideTypicalRange(size) === null;
  return Object.values(sizes).every(isAccepted) ? sizes : null;
};

/** Each axle's size as the request sends it: registered on the car, or given by the customer. */
export const getRequestSizes = (
  car: CarWithTireDimensions,
  season: TireSeason,
  sent: TireSizesToSend | null
): { axle: TireAxle; size: string | null }[] =>
  AXLES.map((axle) => {
    const given = axle === CarAxleType.FRONT ? sent?.frontTireDimension : sent?.rearTireDimension;
    return { axle, size: getRegisteredSize(car, season, axle) ?? given ?? null };
  });
